import { useEffect, useRef, useState } from "react";
import { SignJWT } from "jose";

const secretKey = import.meta.env.VITE_DELUXE_SECRET_KEY;
const fallbackAccessToken = import.meta.env.VITE_DELUXE_ACCESS_TOKEN;

/**
 * Renders the Deluxe Embedded Payments form (credit card tokenization).
 * Mount this component to initialize the form; unmount to destroy it.
 *
 * Props:
 *   amount     {number}   Invoice total (used for JWT payload)
 *   merchantId {string}   Deluxe merchant/partner ID from client settings
 *   customer   {object}   Optional: { firstName, lastName, billingAddress }
 *   onSuccess  {fn}       Called with { Token, NameOnCard, ExpMonthYear, jwt, CardType, MaskedPan }
 *   onError    {fn}       Called with error message string
 *   onCancel   {fn}       Called when user cancels
 */
export default function EmbeddedPaymentForm({
  amount,
  merchantId,
  customer,
  onSuccess,
  onError,
  onCancel,
}) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const successFiredRef = useRef(false);

  useEffect(() => {
    let destroyed = false;
    successFiredRef.current = false;
    setLoading(true);

    const tryInit = async () => {
      // Wait up to 10s for the Deluxe SDK to load from index.html script tag
      let attempts = 0;
      while (!window.EmbeddedPayments && attempts < 100) {
        await new Promise((r) => setTimeout(r, 100));
        attempts++;
      }

      if (!window.EmbeddedPayments) {
        if (!destroyed) {
          onError(
            "Embedded Payments script failed to load. Please refresh the page.",
          );
        }
        return;
      }

      if (destroyed) return;

      try {
        const secret = new TextEncoder().encode(secretKey);
        const payload = {
          accessToken: merchantId || fallbackAccessToken,
          amount,
          generateToken: true,
          requireCvv: true,
          hideproductspanel: true,
          hidesummarypanel: true,
          hidetermsandconditions: true,
        };

        if (customer?.firstName) payload.firstName = customer.firstName;
        if (customer?.lastName) payload.lastName = customer.lastName;
        if (customer?.billingAddress)
          payload.billingAddress = customer.billingAddress;

        const jwt = await new SignJWT(payload)
          .setProtectedHeader({ alg: "HS256" })
          .setIssuedAt()
          .sign(secret);

        if (destroyed) return;

        await window.EmbeddedPayments.init(jwt, {
          paymentMethods: ["cc"],
          supportedNetworks: ["visa", "mastercard", "amex", "discover"],
        });

        if (destroyed) return;

        window.EmbeddedPayments.setEventHandlers({
          onTokenSuccess: (_gateway, data) => {
            if (successFiredRef.current) return;
            successFiredRef.current = true;
            onSuccess({
              Token: data.Token || data.token || "",
              NameOnCard: data.NameOnCard || data.nameOnCard || "",
              ExpMonthYear: data.ExpMonthYear || data.expMonthYear || "",
              jwt: data.jwt || "",
              CardType: data.CardType || data.cardType,
              MaskedPan: data.MaskedPan || data.maskedPan,
            });
          },
          onTokenFailed: (_gateway, data) => {
            onError(
              data.message || data.error || "Payment tokenization failed",
            );
          },
          onTxnSuccess: (_gateway, data) => {
            if (successFiredRef.current) return;
            successFiredRef.current = true;
            onSuccess({
              Token: data.Token || data.token || "",
              NameOnCard: data.NameOnCard || data.nameOnCard || "",
              ExpMonthYear: data.ExpMonthYear || data.expMonthYear || "",
              jwt: data.jwt || "",
              CardType: data.CardType || data.cardType,
              MaskedPan: data.MaskedPan || data.maskedPan,
            });
          },
          onTxnFailed: (_gateway, data) => {
            onError(data.message || data.error || "Payment Failed");
          },
          onValidationError: (_gateway, errors) => {
            console.warn("Embedded Payments validation error:", errors);
          },
          onCancel: () => onCancel?.(),
        });

        window.EmbeddedPayments.render({
          containerId: "embedded-payments-container",
          paymentpanelcolor: "#ffffff",
          paymentfontcolor: "#111827",
          paymentlabelcolor: "#6b7280",
          paymentinputcolor: "#f1f5f9",
          paymentphcolor: "#94a3b8",
          paybuttoncolor: "#111827",
          paybuttontext: "Add Card",
        });

        if (!destroyed) setLoading(false);
      } catch (err) {
        if (!destroyed) {
          onError(
            err instanceof Error
              ? err.message
              : "Failed to initialize payment form",
          );
          setLoading(false);
        }
      }
    };

    tryInit();

    return () => {
      destroyed = true;
      if (window.EmbeddedPayments?.destroy) {
        try {
          window.EmbeddedPayments.destroy();
        } catch {
          // ignore cleanup errors
        }
      }
    };
  }, []); // run once on mount; caller controls mount/unmount via conditional rendering

  return (
    <div>
      <style>{`
        .embedded-payments-wrapper iframe {
          width: 100% !important;
          min-width: 100% !important;
        }
        @keyframes ep-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {loading && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "32px 0",
            gap: 12,
          }}
        >
          <div style={{ position: "relative", width: 40, height: 40 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "2px solid #e5e7eb",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "2px solid transparent",
                borderTopColor: "#111827",
                animation: "ep-spin 0.8s linear infinite",
              }}
            />
          </div>
          <p style={{ fontSize: 14, color: "#6b7280", fontWeight: 500 }}>
            Preparing secure checkout...
          </p>
        </div>
      )}

      <div
        id="embedded-payments-container"
        ref={containerRef}
        style={{
          minHeight: 200,
          width: "100%",
          borderRadius: 12,
          overflow: "hidden",
        }}
        className="embedded-payments-wrapper"
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          paddingTop: 8,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          style={{ width: 14, height: 14, color: "#9ca3af" }}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2.5" y="7" width="11" height="7.5" rx="1.5" />
          <path d="M5 7V5a3 3 0 0 1 6 0v2" />
        </svg>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>
          Secured by Deluxe Payment Services
        </span>
      </div>
    </div>
  );
}
